/**
 * Barre latérale (design system, BarreLaterale) : recherche et « Nouvelle carte » en haut,
 * sections, arbre des paquets (seule partie qui défile), compte en bas. Sur un écran étroit,
 * seules les sections restent, en tab bar de verre en bas de l'écran.
 */
import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { ArbreDesPaquets, type ArbreDesPaquetsProps } from './ArbreDesPaquets';
import { Bouton } from './Bouton';
import { Icone, type NomIcone } from './Icone';
import { Touche } from './Touche';

export type Section = 'paquets' | 'gestion-des-cartes' | 'statistiques' | 'reglages';

/** `court` : libellé de la tab bar du téléphone, quand le nom complet ne tient pas. */
const SECTIONS: { section: Section; libelle: string; court?: string; icone: NomIcone }[] = [
  { section: 'paquets', libelle: 'Paquets', icone: 'layers' },
  { section: 'gestion-des-cartes', libelle: 'Gestion des cartes', court: 'Cartes', icone: 'list' },
  { section: 'statistiques', libelle: 'Statistiques', icone: 'chart-column' },
  { section: 'reglages', libelle: 'Réglages', icone: 'settings' },
];

export interface BarreLateraleProps {
  compte: string;
  onCompte: (ancre: HTMLElement) => void;
  menuCompteOuvert: boolean;
  onNouvelleCarte: () => void;
  section: Section | null;
  liens: Record<Section, string>;
  onNaviguer: (section: Section) => void;
  arbre: Omit<ArbreDesPaquetsProps, 'idTitre'>;
  onNouveauPaquet: () => void;
  onRechercher: (texte: string) => void;
}

export function BarreLaterale({
  compte,
  onCompte,
  menuCompteOuvert,
  onNouvelleCarte,
  section,
  liens,
  onNaviguer,
  arbre,
  onNouveauPaquet,
  onRechercher,
}: BarreLateraleProps) {
  const defilement = useRef<HTMLDivElement>(null);
  const [defile, setDefile] = useState('');

  useEffect(() => {
    const zone = defilement.current;
    if (!zone) return;
    const mesurer = () => {
      const haut = zone.scrollTop > 0;
      const bas = zone.scrollTop + zone.clientHeight < zone.scrollHeight - 1;
      setDefile([haut && 'haut', bas && 'bas'].filter(Boolean).join(' '));
    };
    mesurer();
    zone.addEventListener('scroll', mesurer);
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(zone);
    return () => {
      zone.removeEventListener('scroll', mesurer);
      observateur.disconnect();
    };
  }, []);

  const lien = (e: MouseEvent<HTMLAnchorElement>, s: Section) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onNaviguer(s);
  };
  const [recherche, setRecherche] = useState('');
  const rechercher = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onRechercher(recherche.trim());
    setRecherche('');
  };

  return (
    <nav
      aria-label="Navigation principale"
      className="fc-barre-laterale"
      data-defile={defile || undefined}
    >
      <div className="fc-barre-laterale__haut">
        <form role="search" className="fc-barre-laterale__recherche" onSubmit={rechercher}>
          <div className="fc-champ fc-champ--compact fc-champ-recherche">
            <Icone nom="search" className="fc-champ__icone" />
            <input
              className="fc-champ__saisie"
              type="search"
              aria-label="Rechercher dans les cartes"
              placeholder="Rechercher"
              autoComplete="off"
              spellCheck={false}
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
          </div>
        </form>
        <button
          type="button"
          className="fc-bouton fc-bouton--secondaire fc-bouton--compact fc-bouton--large fc-barre-laterale__nouvelle"
          aria-keyshortcuts="N"
          title="Nouvelle carte (N)"
          onClick={onNouvelleCarte}
        >
          <Icone nom="plus" />
          Nouvelle carte
          <Touche>N</Touche>
        </button>
      </div>
      <div className="fc-barre-laterale__defilement" ref={defilement}>
        <ul className="fc-barre-laterale__liens">
          {SECTIONS.map(({ section: s, libelle, court, icone }) => (
            <li key={s}>
              <a
                className="fc-barre-laterale__lien"
                href={liens[s]}
                aria-current={section === s ? 'page' : undefined}
                onClick={(e) => lien(e, s)}
              >
                <Icone nom={icone} />
                <span className="fc-barre-laterale__libelle" data-court={court}>
                  {libelle}
                </span>
              </a>
            </li>
          ))}
        </ul>
        <section aria-labelledby="barre-laterale-paquets" className="fc-barre-laterale__section">
          <div className="fc-barre-laterale__entete">
            <h2 className="fc-barre-laterale__intitule" id="barre-laterale-paquets">
              Vos paquets
            </h2>
            <Bouton
              variante="discret"
              taille="petite"
              iconeSeule
              icone="plus"
              etiquette="Nouveau paquet"
              onClick={onNouveauPaquet}
            />
          </div>
          <ArbreDesPaquets {...arbre} idTitre="barre-laterale-paquets" />
        </section>
      </div>
      <div className="fc-barre-laterale__bas">
        <button
          type="button"
          className="fc-bouton fc-bouton--discret fc-bouton--compact fc-barre-laterale__compte"
          aria-haspopup="menu"
          aria-expanded={menuCompteOuvert}
          title={compte}
          onClick={(e) => onCompte(e.currentTarget)}
        >
          <Icone nom="user" />
          <span className="fc-barre-laterale__nom-compte">{compte}</span>
          <Icone nom="chevron-up" className="fc-barre-laterale__chevron" />
        </button>
      </div>
    </nav>
  );
}
